"use client";

// Splash / "/" route — shown while FlowGate decides where to send the user.
// Mirrors Flutter splash_screen.dart loading state: logo + app name + spinner.

import { useApp } from "@/store/appStore";
import { useT } from "@/lib/i18n/useT";
import { electionTitle } from "@/lib/branding";
import { localizeError } from "@/lib/errorMessage";

export default function Splash() {
  const t = useT();
  const status = useApp((s) => s.auth.status);
  const error = useApp((s) => s.auth.error);
  const restoreSession = useApp((s) => s.restoreSession);

  // restoreSession gives up after 3 tries and parks auth in "error", which
  // deriveFlowState maps back to "checkingAuth" -> here, and FlowGate then holds
  // the route. Without a way out that is a permanent spinner: nothing retries on
  // its own, and a voter on a shared phone will not think to hard-refresh.
  // splash_screen.dart:36 always offered the message and a Retry.
  if (status === "error") {
    return (
      <main className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-100 text-4xl">
          ⚠️
        </div>
        <h1 className="mt-6 text-xl font-semibold text-gray-900">
          {electionTitle(t)}
        </h1>
        <p className="mt-3 text-sm text-red-600">{localizeError(error, t)}</p>
        <button
          type="button"
          onClick={() => void restoreSession()}
          className="mt-8 w-full max-w-xs rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
        >
          {t("retry")}
        </button>
        <p className="mt-6 max-w-xs text-xs text-gray-500">
          {t("persistentErrorHelp")}
        </p>
      </main>
    );
  }

  return (
    <main className="flex flex-1 flex-col items-center justify-center px-6 text-center">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/icon.png"
        alt={electionTitle(t)}
        width={128}
        height={128}
        className="h-32 w-32 rounded-2xl object-contain"
      />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight text-foreground">
        {electionTitle(t)}
      </h1>
      <div
        className="mt-8 h-8 w-8 animate-spin rounded-full border-4 border-primary-light border-t-primary"
        role="status"
        aria-label={t("loading")}
      />
      <p className="mt-4 text-sm text-muted">{t("loading")}</p>
    </main>
  );
}
